/**
 * /dm — DM inbox.
 *
 * Premium-only viewer: free users see the thread list as a paywall card
 * with the count of waiting messages + distinct senders, plus a CTA
 * that opens the shared PaywallSheet. Sender identities + message
 * contents stay hidden — the design intent is that the reveal happens
 * inside the thread view, which a free user can't reach.
 *
 * Premium users see the existing list of threads with last-message
 * preview + unread count + relative timestamp; tap a row → /dm/:threadId.
 *
 * Empty state nudges the user toward rating profiles (the only way to
 * unlock a new DM target as the initiator is to have rated the person).
 *
 * No WebSocket subscription on this screen — opening / refreshing the
 * thread list re-pulls inbox state. WS only spins up inside DMThread
 * where realtime matters.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { dmApi, type DMInboxSummary, type DMThread } from '@/lib/api';
import { AppTopbar, Btn, Eyebrow, Icons } from '@/components';
import { PaywallSheet } from '@/components/PaywallSheet';
import { useAppContext } from '@/components/AppShell';
import { friendlyError } from '@/lib/errors';

export function DMPage() {
  const { user } = useAppContext();
  const [threads, setThreads] = useState<DMThread[] | null>(null);
  // Free callers get a metadata-only summary instead of full threads;
  // the backend hides sender names + message bodies from the wire.
  // Kept separate from `threads` so the paywall can render the
  // "N messages waiting" pitch without us needing thread rows.
  const [summary, setSummary] = useState<DMInboxSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [paywallOpen, setPaywallOpen] = useState(false);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const r = await dmApi.listThreads();
      setThreads(r.threads ?? []);
      setSummary(r.summary ?? null);
    } catch (e) {
      setErr(friendlyError(e, 'DM'));
      setThreads([]);
      setSummary(null);
    }
  }, []);

  useEffect(() => {
    // Auth gate lives in AppShell.
    load();
  }, [load]);

  const isPremium = user.tier === 'premium';
  const hasThreads = !!threads && threads.length > 0;
  // Free + has waiting messages → the dark FOMO paywall card.
  // Free + zero waiting → the soft empty-state pitch.
  // Both rely on `summary` (free callers can't derive counts from
  // `threads` any more — that array is empty by design).
  const hasWaiting = !!summary && summary.thread_count > 0;

  return (
    <>
      <AppTopbar eyebrow="Messages" />
      <main className="dm-inbox-shell" style={page}>
      {err && <div style={errorBox}>{err}</div>}

      {threads === null && !err && <p style={dim}>Loading…</p>}

      {/* Empty state — both tiers share the italic "No conversations
          yet" header. Free users get a short premium nudge + Get
          premium button stacked beneath; premium users see just the
          header. Keeps the surface visually consistent across tiers. */}
      {/* Empty state — free + zero waiting messages, OR premium + no
          threads. The "you have something waiting" pitch lives in
          FreeTierPaywall below. */}
      {threads && !err && !hasThreads && !hasWaiting && (
        <EmptyState
          isPremium={isPremium}
          onUpgrade={() => setPaywallOpen(true)}
        />
      )}

      {/* Free-tier paywall — fires whenever the summary tells us
          there are waiting messages. Driven by the backend summary
          (not the empty threads array, which free callers always get
          empty now). */}
      {!isPremium && hasWaiting && summary && (
        <FreeTierPaywall summary={summary} onUpgrade={() => setPaywallOpen(true)} />
      )}

      <PaywallSheet
        open={paywallOpen}
        trigger="dm_open"
        onClose={() => setPaywallOpen(false)}
      />

      {threads && hasThreads && isPremium && (
        <div style={threadList}>
          {threads.map((t) => (
            <Link key={t.id} to={`/dm/${t.id}`} style={threadRowLink}>
              <ThreadRow thread={t} />
            </Link>
          ))}
        </div>
      )}
      </main>
    </>
  );
}

// EmptyState — both tiers share the italic "No conversations yet"
// header. Free users get a short premium nudge + Get premium button
// stacked beneath; premium users see just the header. FreeTierPaywall
// below still handles the "you HAVE waiting DMs but can't read them"
// FOMO case, which is a distinct, louder pitch.
function EmptyState({ isPremium, onUpgrade }: { isPremium: boolean; onUpgrade: () => void }) {
  return (
    <div
      style={{
        padding: '80px 16px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 14,
      }}
    >
      <div
        style={{
          color: 'var(--ink-3)',
          fontSize: 22,
          fontStyle: 'italic',
          fontFamily: 'var(--font-display)',
          lineHeight: 1.2,
        }}
      >
        No conversations yet
      </div>
      {!isPremium && (
        <>
          <div
            style={{
              color: 'var(--ink-3)',
              fontSize: 14,
              lineHeight: 1.5,
              maxWidth: 320,
            }}
          >
            DMs are a premium feature. Upgrade to message anyone whose
            profile you've rated.
          </div>
          <button
            type="button"
            onClick={onUpgrade}
            style={{
              marginTop: 4,
              background: 'var(--coral)',
              color: '#fff',
              border: 'none',
              borderRadius: 100,
              padding: '10px 22px',
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
              fontFamily: 'var(--font-ui)',
            }}
          >
            Get premium →
          </button>
        </>
      )}
    </div>
  );
}

function FreeTierPaywall({ summary, onUpgrade }: { summary: DMInboxSummary; onUpgrade: () => void }) {
  const unreadTotal = summary.unread_count;
  const senderCount = summary.sender_count; // == thread_count in v1
  // Pluralise carefully — "1 person · 1 unread" reads better than
  // "1 people · 1 unread"s.
  const senderLabel = senderCount === 1 ? 'person' : 'people';
  // Prefer unread count for the headline (it's the FOMO driver). Fall
  // back to thread count when every message has been previewed via
  // push but nothing is unread.
  const headlineN = unreadTotal > 0 ? unreadTotal : senderCount;
  const headlineNoun = unreadTotal > 0
    ? `new message${unreadTotal === 1 ? '' : 's'}`
    : `conversation${senderCount === 1 ? '' : 's'}`;

  return (
    <div
      style={{
        background: 'var(--ink)',
        color: 'var(--bg)',
        borderRadius: 28,
        padding: 32,
        boxShadow: 'var(--shadow-card)',
        marginTop: 8,
        animation: 'rise 0.4s ease',
      }}
    >
      <Eyebrow tone="coral" style={{ marginBottom: 12 }}>Premium · DMs</Eyebrow>
      <h2
        className="serif"
        style={{
          fontSize: 48,
          fontWeight: 500,
          letterSpacing: -1.2,
          lineHeight: 1.02,
          margin: 0,
        }}
      >
        <span style={{ color: 'var(--coral)' }}>{headlineN}</span> {headlineNoun}{' '}
        <span style={{ fontStyle: 'italic' }}>waiting for you.</span>
      </h2>
      <p style={{ fontSize: 15, opacity: 0.75, marginTop: 14, lineHeight: 1.5, maxWidth: 460 }}>
        {senderCount === 1 ? 'Someone' : `${senderCount} ${senderLabel}`} who rated your profile
        {senderCount === 1 ? ' has' : ' have'} reached out. Upgrade to read who, what they said,
        and reply.
      </p>

      <div
        style={{
          marginTop: 22,
          display: 'flex',
          alignItems: 'center',
          gap: 18,
          flexWrap: 'wrap',
        }}
      >
        <Btn
          variant="primary"
          size="lg"
          icon={<Icons.spark size={16} />}
          onClick={onUpgrade}
        >
          Get premium
        </Btn>
        <div style={{ fontSize: 12, opacity: 0.55 }}>Cancel anytime · One tap to upgrade</div>
      </div>

      {/* Faux thread cards underneath — gives the screen visual weight
          without leaking names or content. Card count is min(threads,
          3) since the backend no longer returns the threads array;
          we're drawing pure placeholders shaped like the real
          ThreadRow purely for layout density. */}
      <div
        style={{
          marginTop: 28,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {Array.from({ length: Math.min(senderCount, 3) }).map((_, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: '14px 16px',
              background: 'rgba(255,255,255,0.06)',
              borderRadius: 14,
              opacity: 1 - i * 0.18,
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 38,
                background: 'rgba(255,255,255,0.14)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'rgba(255,255,255,0.6)',
              }}
            >
              <Icons.lock size={14} />
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div
                style={{
                  height: 10,
                  borderRadius: 4,
                  background: 'rgba(255,255,255,0.16)',
                  width: '40%',
                }}
              />
              <div
                style={{
                  height: 8,
                  borderRadius: 4,
                  background: 'rgba(255,255,255,0.10)',
                  width: '78%',
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ThreadRow({ thread }: { thread: DMThread }) {
  const otherName = thread.other_user.display_name || 'Someone';
  const initial = otherName.charAt(0).toUpperCase();
  return (
    <div style={threadRow}>
      <div style={avatar}>
        {thread.other_user.avatar_url ? (
          <img
            src={thread.other_user.avatar_url}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <span style={avatarInitial}>{initial}</span>
        )}
      </div>
      <div style={threadBody}>
        <div style={threadHeaderRow}>
          <span style={threadName}>{otherName}</span>
          <span style={threadWhen}>{relativeWhen(thread.last_message_at)}</span>
        </div>
        <div style={threadPreviewRow}>
          <span style={threadPreview}>
            {thread.last_message
              ? thread.last_message.body
              : <span style={{ fontStyle: 'italic', color: 'var(--ink-3)' }}>(no messages yet)</span>}
          </span>
          {thread.unread_count > 0 && (
            <span style={unreadBadge}>{thread.unread_count}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function relativeWhen(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = ms / 60_000;
  if (m < 1) return 'just now';
  if (m < 60) return `${Math.round(m)}m`;
  const h = m / 60;
  if (h < 24) return `${Math.round(h)}h`;
  const d = h / 24;
  if (d < 7) return `${Math.round(d)}d`;
  const w = d / 7;
  if (w < 5) return `${Math.round(w)}w`;
  return new Date(iso).toLocaleDateString();
}

// ── styles ────────────────────────────────────────────────────────────

const page: React.CSSProperties = { maxWidth: 720, margin: '0', padding: '0 40px 60px' };

const dim: React.CSSProperties = { color: 'var(--ink-3)', padding: 32, textAlign: 'center' };
const errorBox: React.CSSProperties = {
  background: 'rgba(208,64,48,0.08)', color: '#a8331f',
  border: '1px solid rgba(208,64,48,0.2)', borderRadius: 12,
  padding: '10px 14px', fontSize: 13, marginBottom: 16,
};


const threadList: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 0 };
const threadRowLink: React.CSSProperties = { textDecoration: 'none', color: 'inherit' };
const threadRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 14,
  padding: '14px 12px',
  borderBottom: '1px solid var(--line)',
};
const avatar: React.CSSProperties = {
  width: 44, height: 44, borderRadius: 44, overflow: 'hidden',
  background: 'var(--chip)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', flexShrink: 0,
};
const avatarInitial: React.CSSProperties = { fontFamily: 'serif', fontStyle: 'italic', fontSize: 18, color: 'var(--ink-2)' };
const threadBody: React.CSSProperties = { flex: 1, minWidth: 0 };
const threadHeaderRow: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', marginBottom: 4 };
const threadName: React.CSSProperties = { fontWeight: 600, fontSize: 15, color: 'var(--ink)' };
const threadWhen: React.CSSProperties = { fontSize: 11, color: 'var(--ink-3)', fontFamily: 'ui-monospace, monospace' };
const threadPreviewRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10 };
const threadPreview: React.CSSProperties = {
  flex: 1, fontSize: 13, color: 'var(--ink-3)',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};
const unreadBadge: React.CSSProperties = {
  background: 'var(--coral)', color: '#fff',
  fontSize: 11, fontWeight: 600,
  borderRadius: 100, padding: '2px 8px', minWidth: 18,
  textAlign: 'center',
};
